import { AUDIENCE_SCOPE, NOTIFICATION_CATEGORY, NOTIFICATION_SEVERITY } from './Enums';

/**
 * One notification as it appears in the caller's own inbox.
 *
 * Not a BaseEntity: this is a per-caller view rather than a row. The notification itself is shared
 * by everybody it is addressed to, and `read`/`dismissed` come from a receipt written against the
 * caller's username — which is why dismissing is a POST that hides it for one person, and never a
 * DELETE. See NotificationApi.
 */
export interface Notification {
  id: number;
  /** The catalog constant the service raised this under, e.g. `AUTOMATION_FAILURE`. */
  type: string;
  /** Null for a type this service no longer knows, and for anything the websocket delivered. */
  category: NOTIFICATION_CATEGORY | null;
  severity: NOTIFICATION_SEVERITY;
  title: string;
  message: string | null;
  /** An entity name such as `INSTRUCTOR` or `KLASS`, when the notification is about one. */
  targetEntity: string | null;
  targetId: number | null;
  audienceScope: AUDIENCE_SCOPE;
  /** A username for USER, a role for ROLE, null for ALL. */
  audienceValue: string | null;
  createdAt: string;
  /** `system` for anything a Kafka consumer or a scheduled job raised. */
  createdBy: string | null;
  expiresAt: string | null;
  read: boolean;
  readAt: string | null;
  /**
   * Always false in a list, which by definition excludes what the caller has dismissed. Only the
   * single-item endpoints can return it true.
   */
  dismissed: boolean;
  dismissedAt: string | null;
}

/**
 * What arrives on /ws/notifications: the notification, without anything that is per-caller.
 *
 * A strict subset of Notification. `read` and `dismissed` are absent because the push carries no
 * receipt — a pushed notification is new, so it is unread by construction — and `category` and
 * `expiresAt` are absent because the envelope is built for delivery rather than display.
 */
export interface NotificationEnvelope {
  id: number;
  type: string;
  severity: NOTIFICATION_SEVERITY;
  audienceScope: AUDIENCE_SCOPE;
  audienceValue: string | null;
  title: string;
  message: string | null;
  targetEntity: string | null;
  targetId: number | null;
  createdBy: string | null;
  createdAt: string;
}

/**
 * A pushed envelope as an inbox entry.
 *
 * The fields the envelope does not carry are filled in with what is true of a notification that
 * has just been pushed: unread, not dismissed, and no expiry known here. `category` stays null
 * rather than being guessed from the type — the catalog that maps one to the other lives in the
 * service precisely so that adding a type needs no change on this side, and a copy of it here
 * would be the thing that goes stale. Nothing in the panel renders it.
 */
export const notificationFromEnvelope = (envelope: NotificationEnvelope): Notification => ({
  ...envelope,
  category: null,
  expiresAt: null,
  read: false,
  readAt: null,
  dismissed: false,
  dismissedAt: null,
});
